import React, { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import FilterIcon from './FilterIcon';
import {
  API_DATE_FORMAT,
  CalendarCell,
  clampDateRange,
  formatDisplayDate,
  getCalendarMonth,
  getQuickDateRanges,
  QuickDateRange,
} from './date';

export interface FilterDateRangeProps {
  fromDate: Date | null;
  toDate: Date | null;
  onChange: (fromDate: Date, toDate: Date) => void;
  onDone: () => void;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const FilterDateRange: React.FC<FilterDateRangeProps> = ({ fromDate, toDate, onChange, onDone }) => {
  const [viewMonth, setViewMonth] = useState<Moment>(() =>
    fromDate ? moment(fromDate).startOf('month') : moment().startOf('month')
  );
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const quickRanges = useMemo(() => getQuickDateRanges(), []);

  useEffect(() => {
    if (fromDate) {
      setViewMonth(moment(fromDate).startOf('month'));
    }
  }, [fromDate]);

  const previewFrom = pendingStart || fromDate;
  const previewTo = pendingStart ? hoverDate || pendingStart : toDate;
  const preview = previewFrom && previewTo ? clampDateRange(previewFrom, previewTo) : { fromDate: '', toDate: '' };
  const calendar = getCalendarMonth(viewMonth, preview.fromDate, preview.toDate);

  const selectQuickRange = (range: QuickDateRange) => {
    const start = moment(range.start, API_DATE_FORMAT, true).toDate();
    const end = moment(range.end, API_DATE_FORMAT, true).toDate();
    setPendingStart(null);
    setHoverDate(null);
    onChange(start, end);
    onDone();
  };

  const selectDay = (cell: CalendarCell) => {
    const selectedDate = moment(cell.date, API_DATE_FORMAT, true).toDate();

    if (!pendingStart) {
      setPendingStart(selectedDate);
      setHoverDate(selectedDate);
      return;
    }

    const range = clampDateRange(pendingStart, selectedDate);
    setPendingStart(null);
    setHoverDate(null);
    onChange(
      moment(range.fromDate, API_DATE_FORMAT, true).toDate(),
      moment(range.toDate, API_DATE_FORMAT, true).toDate()
    );
    onDone();
  };

  const changeMonth = (amount: number) => {
    setViewMonth((current) => current.clone().add(amount, 'month'));
  };

  const changeYear = (amount: number) => {
    setViewMonth((current) => current.clone().add(amount, 'year'));
  };

  return (
    <div className="sf-datepicker">
      <div className="sf-datepicker__quick">
        <div className="sf-datepicker__quick-group">
          <span className="sf-datepicker__quick-label">Quick dates</span>
          <div className="sf-datepicker__chips">
            {quickRanges
              .filter((range) => range.group === 'recent')
              .map((range) => (
                <button key={range.key} type="button" className="sf-chip" onClick={() => selectQuickRange(range)}>
                  {range.label}
                </button>
              ))}
          </div>
        </div>

        <div className="sf-datepicker__quick-group">
          <span className="sf-datepicker__quick-label">Months</span>
          <div className="sf-datepicker__chips">
            {quickRanges
              .filter((range) => range.group === 'month')
              .map((range) => (
                <button key={range.key} type="button" className="sf-chip" onClick={() => selectQuickRange(range)}>
                  {range.label}
                </button>
              ))}
          </div>
        </div>

        <div className="sf-datepicker__quick-group">
          <span className="sf-datepicker__quick-label">Seasons &amp; years</span>
          <div className="sf-datepicker__chips">
            {quickRanges
              .filter((range) => range.group === 'season' || range.group === 'year')
              .map((range) => (
                <button key={range.key} type="button" className="sf-chip" onClick={() => selectQuickRange(range)}>
                  {range.label}
                </button>
              ))}
          </div>
        </div>
      </div>

      <div className="sf-datepicker__calendar">
        <div className="sf-datepicker__nav">
          <button type="button" className="sf-icon-button" onClick={() => changeYear(-1)} aria-label="Previous year">
            <span className="sf-datepicker__double">
              <FilterIcon name="chevron-left" size={14} />
              <FilterIcon name="chevron-left" size={14} />
            </span>
          </button>
          <button type="button" className="sf-icon-button" onClick={() => changeMonth(-1)} aria-label="Previous month">
            <FilterIcon name="chevron-left" size={16} />
          </button>
          <span className="sf-datepicker__month" aria-live="polite">
            {calendar.monthLabel}
          </span>
          <button type="button" className="sf-icon-button" onClick={() => changeMonth(1)} aria-label="Next month">
            <FilterIcon name="chevron-right" size={16} />
          </button>
          <button type="button" className="sf-icon-button" onClick={() => changeYear(1)} aria-label="Next year">
            <span className="sf-datepicker__double">
              <FilterIcon name="chevron-right" size={14} />
              <FilterIcon name="chevron-right" size={14} />
            </span>
          </button>
        </div>

        <div className="sf-datepicker__weekdays" aria-hidden="true">
          {WEEKDAYS.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>

        <div className="sf-datepicker__grid" role="grid" aria-label={calendar.monthLabel}>
          {calendar.cells.map((cell) => {
            const isStart = calendar.isRangeStart(cell.date);
            const isEnd = calendar.isRangeEnd(cell.date);
            const classNames = [
              'sf-datepicker__day',
              !cell.inMonth ? 'is-outside' : '',
              cell.isToday ? 'is-today' : '',
              calendar.isInRange(cell.date) ? 'is-in-range' : '',
              isStart ? 'is-range-start' : '',
              isEnd ? 'is-range-end' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={cell.key}
                type="button"
                role="gridcell"
                className={classNames}
                onClick={() => selectDay(cell)}
                onMouseEnter={() => pendingStart && setHoverDate(moment(cell.date, API_DATE_FORMAT, true).toDate())}
                aria-label={moment(cell.date).format('dddd, D MMMM YYYY')}
                aria-selected={isStart || isEnd}
              >
                {cell.dayNumber}
              </button>
            );
          })}
        </div>

        <p className="sf-datepicker__hint">
          {pendingStart
            ? `Start ${formatDisplayDate(pendingStart)} — choose an end date`
            : 'Choose a start date, or pick a quick range above.'}
        </p>
      </div>
    </div>
  );
};

export default FilterDateRange;
